import { ENGINE_PROVIDER } from "@/lib/engine";
import { resolveRecalculate } from "@/lib/recalculate";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Check, Search, X } from "lucide-react";
import type { Account, Backtest } from "@/api/types";
import { cn } from "@/lib/utils";
import { DateTimePicker } from "../DateTimePicker";
import { ExchangeLeverageNotice } from "../ExchangeLeverageNotice";
import { ExchangeMark, fieldLabel } from "../ui/kit";
import { accountKindLabel, accountUsdBalance, isFuturesAccount } from "@/lib/account";
import { engineType, isLabInstance, isLiveInstance, marketLabel } from "@/lib/live";
import { toDateTime, todayUtcStart } from "@/lib/backtest-run";
import { isMarketProviderEnabled } from "@/lib/market-provider";
import { useApi } from "@/context/ApiContext";

const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

function parseLimit(val: string): number | null {
  const t = val.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** On/off switch (design `.tg`) with a title and hint (design `.toggle-row`). */
function ToggleRow({
  on,
  onChange,
  title,
  hint,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  title: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="flex items-start gap-3 rounded-[12px] border border-[var(--border)] p-3 text-left"
    >
      <span
        className={`relative mt-0.5 h-5 w-[34px] shrink-0 rounded-full border ${
          on
            ? "border-[var(--primary)] bg-[var(--primary)]"
            : "border-[var(--border)] bg-[var(--bg-hover)]"
        }`}
      >
        <i
          className={`absolute top-[2px] h-3.5 w-3.5 rounded-full transition-[left] ${
            on ? "left-[16px] bg-white" : "left-[2px] bg-[var(--text-muted)]"
          }`}
        />
      </span>
      <span className="min-w-0">
        <b className="block text-[13px] font-semibold text-[var(--text-primary)]">{title}</b>
        <span className="text-xs text-[var(--text-muted)]">{hint}</span>
      </span>
    </button>
  );
}

/** Selectable card (design `.acc`). */
function Choice({
  on,
  disabled,
  onClick,
  children,
}: {
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={on}
      className={`flex flex-col gap-2 rounded-[12px] border bg-[var(--bg-card-elevated)] p-3 text-left disabled:cursor-not-allowed disabled:opacity-55 ${
        on
          ? "border-[var(--primary)] shadow-[inset_0_0_0_1px_var(--primary)]"
          : "border-[var(--border)] hover:bg-[var(--bg-hover)]"
      }`}
    >
      {children}
    </button>
  );
}

const STEPS = ["Backtest", "Exchange", "Money & risk", "Review & start"] as const;

const STEP_HINT = [
  "The live strategy runs this backtest's script, market and settings.",
  "Only accounts of the backtest's kind (spot or futures) can run it.",
  "Order size and limits can be changed later on the strategy's page.",
  "Creates the strategy switched off. You land on its page to start it.",
] as const;

/** Past this many backtests, step 1 offers a search box. */
const SEARCH_FROM = 6;

/**
 * "Go live" dialog, rendered by Shell over whatever page is open (like New
 * backtest): picks a lab backtest, an exchange and the order size, then
 * creates the live strategy switched off and opens its page.
 */
export default function GoLiveModal({
  backtestId: initialBacktestId = "",
  onClose,
}: {
  backtestId?: string;
  onClose: () => void;
}) {
  const api = useApi(["accounts", "backtests"]);
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  // The Dashboard and Backtests open it with a backtest to preselect.
  const [backtestId, setBacktestId] = useState(initialBacktestId);
  const [step, setStep] = useState(() => (initialBacktestId ? 1 : 0));
  const [query, setQuery] = useState("");
  const [accountId, setAccountId] = useState("");
  const [bypass, setBypass] = useState(true);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [cryptoLimitStr, setCryptoLimitStr] = useState("");
  const [fiatLimitStr, setFiatLimitStr] = useState("");
  const [slippageStr, setSlippageStr] = useState("");
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(() => todayUtcStart());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates: Backtest[] = useMemo(
    () => (api.backtests || []).filter(isLabInstance),
    [api.backtests]
  );
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter((bt) =>
      `${bt.name} ${marketLabel(bt)}`.toLowerCase().includes(needle)
    );
  }, [candidates, query]);
  const selected = useMemo(
    () => candidates.find((bt) => bt.id === backtestId),
    [candidates, backtestId]
  );
  const accounts: Account[] = useMemo(
    () =>
      (api.accounts || []).filter((a: Account) => a.enabled && isMarketProviderEnabled(a.provider)),
    [api.accounts]
  );
  const account = accounts.find((a) => a.id === accountId);
  const wantFutures = selected ? engineType(selected) === "futures" : null;
  const fits = (a: Account) => wantFutures == null || isFuturesAccount(a) === wantFutures;

  useEffect(() => {
    if (!selected) return;
    setStartDate(todayUtcStart());
    setName(`${selected.name} (live)`);
    // Pick the only matching exchange for them.
    const matching = accounts.filter(fits);
    setAccountId((cur) =>
      cur && matching.some((a) => a.id === cur) ? cur : matching.length === 1 ? matching[0]!.id : ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // The date picker opens its own popup on top; let it handle its Escape.
      if (e.key !== "Escape" || loading) return;
      const active = document.activeElement;
      if (active && active !== document.body && !dialogRef.current?.contains(active)) return;
      onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  const liveOn = (id: string) =>
    (api.backtests || []).filter(
      (bt: Backtest) =>
        isLiveInstance(bt) && bt.enabled && bt.accounts?.some((a) => a.account_id === id)
    ).length;

  const summaries = [
    selected ? `${selected.name}` : "",
    account ? `${account.name}` : "",
    bypass ? "Script's order size" : fiatLimitStr ? `Up to ${fiatLimitStr}` : "Capped",
    "",
  ];
  const canReach = (i: number) =>
    i === 0 || (i === 1 && !!selected) || (i >= 2 && !!selected && !!account);

  async function handleCreate() {
    if (!selected) return setError("Select a backtest.");
    if (!accountId) return setError("Select an exchange.");
    if (!name.trim()) return setError("Give the strategy a name.");
    if (!startDate) return setError("Select a start date.");

    setLoading(true);
    setError(null);
    try {
      // What the script declares now: the backtest may not have run since it changed.
      const recalculate = await resolveRecalculate(
        api.commands,
        selected.strategy_provider,
        selected.strategy_config,
        selected.recalculate
      );
      const created = await api.commands.createBacktest({
        name: name.trim(),
        description: selected.description || "",
        market_provider: selected.market_provider,
        market_config: selected.market_config || {},
        symbol: selected.symbol,
        timeframe: selected.timeframe,
        strategy_provider: selected.strategy_provider,
        strategy_config: selected.strategy_config || {},
        engine_provider: ENGINE_PROVIDER,
        engine_config: selected.engine_config || {},
        start_date: toDateTime(startDate),
        ...(recalculate ? { recalculate } : {}),
        ...(selected.bar_magnifier ? { bar_magnifier: selected.bar_magnifier } : {}),
        metadata: {
          ui: {
            mode: "live",
            source_id: selected.id,
            ...(typeof selected.metadata?.ui?.script_id === "string"
              ? { script_id: selected.metadata.ui.script_id }
              : {}),
          },
        },
        enabled: false,
      });

      await api.commands.addBacktestAccount({
        backtest_id: created.id,
        account_id: accountId,
        bypass,
        reduce_only: reduceOnly,
        crypto_limit: bypass ? null : parseLimit(cryptoLimitStr),
        fiat_limit: bypass ? null : parseLimit(fiatLimitStr),
        slippage: parseLimit(slippageStr),
      });

      onClose();
      navigate(`/live/${created.id}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't create the live strategy");
      setLoading(false);
    }
  }

  // The footer's primary button per step: what it says, whether it's ready, what it does.
  const canNext =
    step === 0
      ? !!selected
      : step === 1
        ? !!account
        : step === 2
          ? true
          : !loading && !!name.trim() && !!startDate;
  const next = () => (step < 3 ? setStep(step + 1) : void handleCreate());

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={() => !loading && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="golive-title"
        className="flex h-[min(700px,calc(100dvh-2rem))] w-full max-w-[760px] flex-col overflow-hidden rounded-[16px] border border-[var(--border)] bg-[var(--bg-card)] shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[var(--border)] px-5 py-4">
          <div className="min-w-0">
            <h2
              id="golive-title"
              className="m-0 truncate text-base font-semibold text-[var(--text-primary)]"
            >
              {selected ? `Go live with “${selected.name}”` : "Go live"}
            </h2>
            <p className="m-0 mt-0.5 text-xs text-[var(--text-muted)]">
              Nothing trades until you press Start on the strategy's page.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            disabled={loading}
            onClick={onClose}
            className="ml-auto rounded-md p-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
          >
            <X size={18} />
          </button>
        </div>

        {!api.ready ? (
          <div className="flex flex-1 items-center justify-center text-sm text-[var(--text-muted)]">
            Loading…
          </div>
        ) : candidates.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
            <b className="text-[15px] text-[var(--text-primary)]">You need a backtest first</b>
            <p className="m-0 text-[13px] text-[var(--text-muted)]">
              Live strategies run a backtest you've already tested.
            </p>
            <Link to="/lab/backtest" className="btn btn-primary" onClick={onClose}>
              Go to Backtests
            </Link>
          </div>
        ) : (
          <>
            {/* One line: what was picked shrinks (cut with …) before a step wraps; phones show only the current step's name. */}
            <ol className="m-0 flex list-none items-center gap-1 overflow-hidden border-b border-[var(--divider)] px-5 py-3">
              {STEPS.map((label, i) => {
                const done = i < step && canReach(i + 1);
                const on = i === step;
                return (
                  <li
                    key={label}
                    className={cn("flex min-w-0 items-center gap-1", on && "shrink-0")}
                  >
                    {i > 0 && (
                      <span className="mx-1 h-px w-3.5 shrink-0 bg-[var(--border)]" aria-hidden />
                    )}
                    <button
                      type="button"
                      disabled={!canReach(i) || loading}
                      onClick={() => setStep(i)}
                      aria-current={on ? "step" : undefined}
                      title={label}
                      className={cn(
                        "flex min-w-0 items-center gap-2 rounded-full py-1 pl-1 pr-2.5 text-[13px] disabled:cursor-default",
                        on
                          ? "bg-[var(--primary-soft)] text-[var(--text-primary)]"
                          : "text-[var(--text-muted)]"
                      )}
                    >
                      <span
                        className={cn(
                          "num grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full text-[11px] font-semibold",
                          on
                            ? "bg-[var(--primary)] text-[var(--on-primary)]"
                            : done
                              ? "bg-[var(--success-soft)] text-[var(--success)]"
                              : "bg-[var(--bg-hover)]"
                        )}
                      >
                        {done ? <Check size={12} strokeWidth={3} /> : i + 1}
                      </span>
                      <span className={cn("shrink-0 whitespace-nowrap", !on && "hidden sm:inline")}>
                        {label}
                      </span>
                      {done && summaries[i] && (
                        <span className="hidden min-w-0 truncate text-xs text-[var(--text-disabled)] sm:inline">
                          · {summaries[i]}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-[18px] [&>*]:shrink-0">
              {error && (
                <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">
                  {error}
                </div>
              )}

              {step === 0 && (
                <>
                  <h3 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">
                    Which backtest?
                  </h3>
                  {candidates.length > SEARCH_FROM && (
                    <label className="relative block">
                      <Search
                        size={14}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
                      />
                      <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name or symbol"
                        aria-label="Search backtests"
                        className="w-full pl-8"
                        autoFocus
                      />
                    </label>
                  )}
                  {shown.length === 0 ? (
                    <p className="m-0 py-6 text-center text-[13px] text-[var(--text-muted)]">
                      No backtest matches “{query}”.
                    </p>
                  ) : (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2.5">
                      {shown.map((bt) => (
                        <Choice
                          key={bt.id}
                          on={bt.id === backtestId}
                          onClick={() => {
                            setBacktestId(bt.id);
                            setStep(1);
                          }}
                        >
                          <span className="flex items-center gap-2.5">
                            <ExchangeMark provider={bt.market_provider} size={22} />
                            <span className="min-w-0">
                              <b className="block truncate text-[13px] font-semibold text-[var(--text-primary)]">
                                {bt.name}
                              </b>
                              <span className="block text-xs text-[var(--text-muted)]">
                                {marketLabel(bt)}
                              </span>
                            </span>
                          </span>
                        </Choice>
                      ))}
                    </div>
                  )}
                </>
              )}

              {step === 1 && selected && (
                <>
                  <h3 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">
                    Where should it trade?
                  </h3>
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2.5">
                    {accounts.map((a) => {
                      const bal = accountUsdBalance(api.balancesByAccount?.[a.id]);
                      const running = liveOn(a.id);
                      const ok = fits(a);
                      return (
                        <Choice
                          key={a.id}
                          on={a.id === accountId}
                          disabled={!ok}
                          onClick={() => setAccountId(a.id)}
                        >
                          <span className="flex items-center gap-2.5">
                            <ExchangeMark provider={a.provider} />
                            <span className="min-w-0">
                              <b className="block truncate text-[13px] font-semibold text-[var(--text-primary)]">
                                {a.name}
                              </b>
                              <span className="block text-xs text-[var(--text-muted)]">
                                {accountKindLabel(a)}
                                {bal ? ` · ${money.format(bal.value)} ${bal.asset}` : ""}
                              </span>
                            </span>
                          </span>
                          <span
                            className={`text-xs ${ok ? "text-[var(--text-muted)]" : "text-[var(--warning)]"}`}
                          >
                            {!ok
                              ? wantFutures
                                ? "Spot account: can't run this futures backtest"
                                : "Futures account: can't run this spot backtest"
                              : running
                                ? `${running} ${running === 1 ? "strategy" : "strategies"} running here`
                                : "Nothing running here yet"}
                          </span>
                        </Choice>
                      );
                    })}
                    <Link
                      to="/integrations/marketplaces/new"
                      onClick={onClose}
                      className="flex min-h-[72px] items-center justify-center rounded-[12px] border border-dashed border-[var(--border)] p-3 text-[13px] font-semibold text-[var(--text-primary)] no-underline hover:bg-[var(--bg-hover)]"
                    >
                      + New account
                    </Link>
                  </div>
                </>
              )}

              {step === 2 && selected && account && (
                <>
                  <h3 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">
                    How big are its orders?
                  </h3>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <Choice on={bypass} onClick={() => setBypass(true)}>
                      <b className="text-[13px] font-semibold text-[var(--text-primary)]">
                        Script's order size
                      </b>
                      <span className="text-xs text-[var(--text-muted)]">
                        Orders use the size the strategy computes, as in the backtest. Set the
                        leverage on the exchange to match.
                      </span>
                    </Choice>
                    <Choice on={!bypass} onClick={() => setBypass(false)}>
                      <b className="text-[13px] font-semibold text-[var(--text-primary)]">
                        Cap what it can use
                      </b>
                      <span className="text-xs text-[var(--text-muted)]">
                        Limit the money and coins this strategy may trade with.
                      </span>
                    </Choice>
                  </div>

                  {!bypass && (
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2.5 rounded-[12px] border border-[var(--border)] bg-[var(--bg-card-elevated)] p-4">
                      <label className="flex flex-col gap-1.5">
                        <span className={fieldLabel}>Money limit</span>
                        <input
                          inputMode="decimal"
                          value={fiatLimitStr}
                          onChange={(e) => setFiatLimitStr(e.target.value)}
                          placeholder="No limit"
                        />
                        {accountUsdBalance(api.balancesByAccount?.[account.id]) && (
                          <span className="text-xs text-[var(--text-muted)]">
                            Free on {account.name}:{" "}
                            {money.format(
                              accountUsdBalance(api.balancesByAccount?.[account.id])!.value
                            )}{" "}
                            {accountUsdBalance(api.balancesByAccount?.[account.id])!.asset}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className={fieldLabel}>Coin limit</span>
                        <input
                          inputMode="decimal"
                          value={cryptoLimitStr}
                          onChange={(e) => setCryptoLimitStr(e.target.value)}
                          placeholder="No limit"
                        />
                        <span className="text-xs text-[var(--text-muted)]">
                          In units of{" "}
                          {String(selected.symbol).replace(/USDT.*|USDC.*/i, "") || "the coin"}.
                        </span>
                      </label>
                    </div>
                  )}

                  {engineType(selected) === "futures" && (
                    <ToggleRow
                      on={reduceOnly}
                      onChange={setReduceOnly}
                      title="Reduce-only exits"
                      hint="Exits can never flip the position by accident."
                    />
                  )}

                  {engineType(selected) === "futures" && bypass && (
                    <ExchangeLeverageNotice
                      symbol={selected.symbol}
                      strategyConfig={selected.strategy_config || {}}
                      accountId={accountId || null}
                      accountLeverages={
                        accountId ? api.leveragesByAccount[accountId] || [] : undefined
                      }
                    />
                  )}

                  <details>
                    <summary className="cursor-pointer text-[13px] text-[var(--text-muted)]">
                      Advanced
                    </summary>
                    <label className="mt-3 flex max-w-[320px] flex-col gap-1.5">
                      <span className={fieldLabel}>Max slippage %</span>
                      <input
                        inputMode="decimal"
                        value={slippageStr}
                        onChange={(e) => setSlippageStr(e.target.value)}
                        placeholder="Market orders"
                      />
                      <span className="text-xs text-[var(--text-muted)]">
                        Empty sends market orders. With a value, entries are limit orders within ±
                        this % of the signal price.
                      </span>
                    </label>
                  </details>
                </>
              )}

              {step === 3 && selected && account && (
                <>
                  <h3 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">
                    Review
                  </h3>
                  <div className="flex flex-col rounded-[12px] border border-[var(--border)] bg-[var(--bg-card-elevated)] px-4 py-1">
                    {[
                      ["Backtest", `${selected.name} · ${marketLabel(selected)}`],
                      ["Exchange", `${account.name} · ${accountKindLabel(account)}`],
                      [
                        "Order size",
                        bypass
                          ? "Script's order size"
                          : [
                              fiatLimitStr && `money ≤ ${fiatLimitStr}`,
                              cryptoLimitStr && `coins ≤ ${cryptoLimitStr}`,
                            ]
                              .filter(Boolean)
                              .join(", ") || "Capped, no limits set",
                      ],
                      ...(engineType(selected) === "futures"
                        ? [["Exits", reduceOnly ? "Reduce-only" : "Normal"]]
                        : []),
                      ["Orders", slippageStr ? `Limit, ±${slippageStr}%` : "Market"],
                    ].map(([k, v]) => (
                      <div
                        key={k}
                        className="flex items-center justify-between gap-3 border-t border-[var(--divider)] py-2.5 text-[13px] first:border-t-0"
                      >
                        <span className="text-[var(--text-muted)]">{k}</span>
                        <span className="text-right text-[var(--text-primary)]">{v}</span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2.5">
                    <label className="flex flex-col gap-1.5">
                      <span className={fieldLabel}>Name</span>
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Live strategy name"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className={fieldLabel}>Start from (UTC)</span>
                      <DateTimePicker value={startDate} onChange={setStartDate} />
                      <span className="text-xs text-[var(--text-muted)]">
                        Replays candles from here, then follows live ones.
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2.5 border-t border-[var(--border)] bg-[var(--bg-card-elevated)] px-5 py-3.5">
              <span className="min-w-0 flex-1 text-xs text-[var(--text-muted)]">
                {STEP_HINT[step]}
              </span>
              {step > 0 && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setStep(step - 1)}
                  className="btn btn-ghost"
                >
                  Back
                </button>
              )}
              <button type="button" disabled={!canNext} onClick={next} className="btn btn-primary">
                {step < 3 ? "Continue" : loading ? "Creating…" : "Create live strategy"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
